// ─────────────────────────────────────────────────────────────────────
// WHO MAY ASK TO BOOK AGAINST THE CLUB'S OWN RULES (#949, client half).
//
// `adminOverride: true` on a booking command drops the buffer, smart
// allocation and the club's court set. Since 2026-10-31 the server asks
// `booking.override_constraints` for it and refuses the WHOLE request (403)
// from a session without it — the right is a /sa matrix tick, so a club can
// narrow it. Tournaments, KM and activities used to send the flag regardless,
// so narrowing the right made every one of those reservations fail with a
// generic toast, even where the club's rules would have allowed them.
//
// So the browser now sends the flag only when the session may ask for it,
// read from the same `/me` capability union the «Tilganger» door and the
// Meldinger gate read. Without it the request goes as an ordinary booking:
// the club's rules decide, and a refusal says it was the rules.
//
// A REQUEST, NOT A DRAWING. The doors treat an unknown union as «no»: drawing
// a door the server refuses is the thing they exist to avoid. Here the
// opposite default is the safe one — while `/me` has not answered (or could
// not), the flag goes as the screen asked and the server decides, which is
// exactly yesterday's behaviour; a refusal then arrives as the 403 below,
// with its own sentence. Withholding it on «unknown» would quietly turn a
// holder's override into a rules-checked booking.
//
// FOR ASKING, NEVER FOR DECIDING. The server guards the flag regardless.
// ─────────────────────────────────────────────────────────────────────

/** Spelled exactly as the API's catalog has it — a test reads both. */
export const BOOKING_OVERRIDE_CONSTRAINTS = 'booking.override_constraints';

export interface OverrideRightInputs {
  /** The capability union `/me` reports for the current scope; null = unknown. */
  capabilities: readonly string[] | null;
  /** Whether the browser talks to the API at all (`isApiMode()`). */
  apiMode: boolean;
  /** StarPoint operating AS the organization under an audited context (#976). */
  operatingAs: boolean;
}

/**
 * May this session ask for `adminOverride`? True, false, or null for unknown.
 *
 *   • Off the API (the memory backend, tests, demos) nothing guards the flag.
 *   • An impersonation context holds every organization-derived capability of
 *     the one organization, and `/me` reports no membership for it.
 *   • Otherwise the union decides, and null means it has not answered.
 */
export function overrideRight(input: OverrideRightInputs): boolean | null {
  if (!input.apiMode) return true;
  if (input.operatingAs) return true;
  if (input.capabilities === null) return null;
  return input.capabilities.includes(BOOKING_OVERRIDE_CONSTRAINTS);
}

/** Whether a request that ASKS for the override sends it: only a known «no» withholds it. */
export function sendsOverride(asked: boolean, right: boolean | null | undefined): boolean {
  return asked && right !== false;
}

/**
 * An equipment intent with its `adminOverride` honoured only for a holder.
 *
 * The equipment modal asks for the override in the calendar's admin mode; the
 * asset-booking routes ask the same right for it (#1206). The intent is copied,
 * never mutated, and an intent that does not ask is returned as it came.
 */
export function withOverrideRight<T extends { adminOverride?: boolean }>(
  intent: T,
  right: boolean | null | undefined,
): T {
  if (intent?.adminOverride !== true || sendsOverride(true, right)) return intent;
  return { ...intent, adminOverride: false };
}

/**
 * The server refused the override itself — 403 naming the right.
 *
 * Its own error, not a `BookingRefusedError`: nothing in the calendar was
 * judged, the request was refused before it, and the sentence for it lives in
 * the catalog (the screen translates the code, the transport does not).
 */
export class OverrideRefusedError extends Error {
  readonly code = 'override_not_held' as const;
  constructor() {
    super('[bookings] adminOverride refused: ' + BOOKING_OVERRIDE_CONSTRAINTS);
    this.name = 'OverrideRefusedError';
  }
}

/** Is this 403 body the override refusal (and not some other right)? */
export function isOverrideRefusal(body: unknown): boolean {
  return (
    !!body &&
    typeof body === 'object' &&
    (body as { error?: unknown }).error === 'forbidden' &&
    (body as { capability?: unknown }).capability === BOOKING_OVERRIDE_CONSTRAINTS
  );
}
